import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { StyledAppSelect } from '@nxsys/components/AppSelect/index.styled';
import { defaultDocumentTypeOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { GetDocumentParams } from '@nxsys/types/models/document';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React, { useEffect } from 'react';

type ReportByKey = 'documentType' | 'receivedOn' | 'expiryDate';

type FormValues = {
  reportBy: ReportByKey[];
  documentType?: string;
  receivedOn?: string;
  expiryDate?: string;
};

type AgencyDocumentsFilterModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onFilter: (params: GetDocumentParams) => void;
};

const REPORT_BY_OPTIONS: { value: ReportByKey; label: string }[] = [
  { value: 'documentType', label: 'Document Type' },
  { value: 'receivedOn', label: 'Received On' },
  { value: 'expiryDate', label: 'Expiry Date' },
];

const AgencyDocumentsFilterModal: React.FC<AgencyDocumentsFilterModalProps> = ({
  title,
  isOpen,
  toggle,
  onFilter,
}: AgencyDocumentsFilterModalProps) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm<FormValues>();
  const reportBy = Form.useWatch('reportBy', form);
  const watchValue = Form.useWatch([], form);

  const selectedReportBy = (reportBy ?? []) as ReportByKey[];

  const ableToClearFilter =
    watchValue &&
    (selectedReportBy.length > 0 ||
      !!watchValue.documentType ||
      !!watchValue.receivedOn ||
      !!watchValue.expiryDate);

  useEffect(() => {
    const updates: Partial<FormValues> = {};

    if (!selectedReportBy.includes('documentType')) {
      updates.documentType = undefined;
    }
    if (!selectedReportBy.includes('receivedOn')) {
      updates.receivedOn = undefined;
    }
    if (!selectedReportBy.includes('expiryDate')) {
      updates.expiryDate = undefined;
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [selectedReportBy.join(',')]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values: FormValues) => {
    // IMPORTANT: we always include these keys (even undefined) so that when
    // the parent merges with previous queryParams, unchecked filters are cleared.
    const params: GetDocumentParams = {
      documentType: undefined,
      receivedOn: undefined,
      expiryDate: undefined,
    };

    if (selectedReportBy.includes('documentType') && values.documentType) {
      params.documentType = values.documentType;
    }

    if (selectedReportBy.includes('receivedOn') && values.receivedOn) {
      params.receivedOn = moment(values.receivedOn).toISOString();
    }

    if (selectedReportBy.includes('expiryDate') && values.expiryDate) {
      params.expiryDate = moment(values.expiryDate).toISOString();
    }

    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item
          name="reportBy"
          label={messages['invoices.reportBy'] ?? 'Report By'}
          initialValue={[]}
        >
          <StyledAppSelect
            mode="multiple"
            options={[...REPORT_BY_OPTIONS]}
            placeholder={messages['form.pleaseSelect']}
            size="large"
            allowClear
          />
        </Form.Item>

        {selectedReportBy.includes('documentType') && (
          <Form.Item
            name="documentType"
            label={messages['common.documentType'] ?? 'Document Type'}
          >
            <StyledAppSelect
              options={defaultDocumentTypeOptions}
              placeholder={messages['form.pleaseSelect']}
              size="large"
              allowClear
            />
          </Form.Item>
        )}

        {selectedReportBy.includes('receivedOn') && (
          <Form.Item
            key="received-on-field"
            name="receivedOn"
            label={messages['common.receivedOn'] ?? 'Received On'}
          >
            <AppDatePicker
              placeholder={messages['common.selectDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  receivedOn: date?.toISOString(),
                });
              }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        )}

        {selectedReportBy.includes('expiryDate') && (
          <Form.Item
            key="expiry-date-field"
            name="expiryDate"
            label={messages['common.expiryDate'] ?? 'Expiry Date'}
          >
            <AppDatePicker
              placeholder={messages['common.selectDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  expiryDate: date?.toISOString(),
                });
              }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default AgencyDocumentsFilterModal;
